import { LLMConfig, ChatMessage } from '../../../types/llm'
import { LLMProviderInterface } from '../llm-service'

export class GoogleProvider implements LLMProviderInterface {
  private config: LLMConfig
  private baseUrl: string

  constructor(config: LLMConfig) {
    this.config = config
    this.baseUrl = config.baseUrl || 'https://generativelanguage.googleapis.com/v1beta'
  }

  private formatMessages(messages: ChatMessage[]): any[] {
    const contents: any[] = []
    
    for (const message of messages) {
      if (message.role === 'system') {
        // Prepend system message to first user message
        continue
      }
      
      contents.push({
        role: message.role === 'user' ? 'user' : 'model',
        parts: [{ text: message.content }]
      })
    }

    // Add system message to first user message if exists
    const systemMessage = messages.find(m => m.role === 'system')
    if (systemMessage && contents.length > 0 && contents[0].role === 'user') {
      contents[0].parts[0].text = `${systemMessage.content}\n\n${contents[0].parts[0].text}`
    }

    return contents
  }

  async chat(messages: ChatMessage[], config?: Partial<LLMConfig>): Promise<string> {
    const model = config?.model || this.config.model
    const response = await fetch(
      `${this.baseUrl}/models/${model}:generateContent?key=${this.config.apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contents: this.formatMessages(messages),
          generationConfig: {
            temperature: config?.temperature || this.config.temperature || 0.7,
            maxOutputTokens: config?.maxTokens || this.config.maxTokens || 2000
          }
        })
      }
    )

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Google API error: ${error}`)
    }

    const data = await response.json() as any
    return data.candidates[0].content.parts[0].text
  }

  async streamChat(
    messages: ChatMessage[],
    config?: Partial<LLMConfig>,
    callbacks?: {
      onToken?: (token: string) => void
      onComplete?: (fullText: string) => void
      onError?: (error: Error) => void
    }
  ): Promise<void> {
    try {
      const model = config?.model || this.config.model
      const response = await fetch(
        `${this.baseUrl}/models/${model}:streamGenerateContent?key=${this.config.apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: this.formatMessages(messages),
            generationConfig: {
              temperature: config?.temperature || this.config.temperature || 0.7,
              maxOutputTokens: config?.maxTokens || this.config.maxTokens || 2000
            }
          })
        }
      )

      if (!response.ok) {
        const error = await response.text()
        throw new Error(`Google API error: ${error}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let fullText = ''

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        
        try {
          // Google returns JSON objects separated by commas
          const jsonStr = chunk.replace(/}{/g, '},{')
          const wrapped = `[${jsonStr}]`
          const parsed = JSON.parse(wrapped)
          
          for (const item of parsed) {
            if (item.candidates?.[0]?.content?.parts?.[0]?.text) {
              const content = item.candidates[0].content.parts[0].text
              fullText += content
              callbacks?.onToken?.(content)
            }
          }
        } catch (e) {
          // Try parsing as single object
          try {
            const parsed = JSON.parse(chunk)
            if (parsed.candidates?.[0]?.content?.parts?.[0]?.text) {
              const content = parsed.candidates[0].content.parts[0].text
              fullText += content
              callbacks?.onToken?.(content)
            }
          } catch (e2) {
            // Skip invalid JSON
          }
        }
      }

      callbacks?.onComplete?.(fullText)
    } catch (error) {
      callbacks?.onError?.(error as Error)
      throw error
    }
  }
}
